'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { Archive, ArrowUpRight, Refrigerator, Ruler, Scale } from '@/components/homepage-october/lucide'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/homepage-october/ui/table'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipRows, Eyebrow, Section, SectionHeader, SubHeader, words } from '../blocks/layout'
import { ProductCard } from '../blocks/ProductCard'
import { ProductRail } from '../blocks/Rail'
import type { Link, Model, Tile } from '../types'

/* ------------------------------------------------------------------ local helpers */

/** Product shots (cut-outs on white) sit contained on a tint; lifestyle photos fill their frame. */
const isCutout = (src: string) => /\/product-images\/|\/tiles\/|\/canvas\/fridge-com-|\/canvas\/equator-|cutout/i.test(src)

/** A soft light glow behind a section's content (decorative, light tints only). */
function Glow({ className }: { className: string }) {
  return <div aria-hidden="true" className={compactClassName(cn('pointer-events-none absolute rounded-full', className))} />
}

/** Rounded outline link with an arrow icon (the model's trailing arrow glyph is dropped). */
function MoreLink({ link, className }: { link: Link; className?: string }) {
  return (
    <Button
      asChild
      variant="outline"
      size="sm"
      className={cn('h-9! rounded-full! border-[#0891b2]/30! bg-white! px-4! font-semibold! text-[#0e6a82]! hover:border-[#0891b2]/60! hover:bg-[#ecf8fc]! hover:text-[#0b5468]!', className)}
    >
      <DestinationLink href={link.href} aria-label={link.aria}>
        {words(link.text)}
        <ArrowRight aria-hidden="true" />
      </DestinationLink>
    </Button>
  )
}

/**
 * A shop tile: the product photo contained on a light tint (multiply blends the photo's white backdrop into the tint),
 * a white label bar under it, lift and zoom on hover.
 */
function ShopTile({ t, tint, hover, arrow }: { t: Tile; tint: string; hover: string; arrow: string }) {
  return (
    <DestinationLink
      href={t.href}
      aria-label={t.aria || undefined}
      className={cn('group flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition duration-300 motion-safe:hover:-translate-y-1', hover)}
    >
      <div className={compactClassName(cn('relative aspect-square overflow-hidden', tint))}>
        <Img
          src={t.img.src}
          alt={t.img.alt}
          className="absolute inset-0 size-full p-[11%] mix-blend-multiply transition-transform duration-500 motion-safe:group-hover:scale-[1.07]"
        />
      </div>
      <div className={compactClassName("flex flex-1 items-center justify-between gap-2 px-3.5 py-3")}>
        <span className={compactClassName("min-w-0 text-[13px] font-semibold leading-snug text-neutral-900 sm:text-sm")}>
          {t.label}
          {t.count ? <span className={compactClassName("tabular ml-1 font-normal text-neutral-500")}>{t.count}</span> : null}
        </span>
        <ArrowRight aria-hidden="true" className={cn('size-4 shrink-0 transition-transform motion-safe:group-hover:translate-x-0.5', arrow)} />
      </div>
    </DestinationLink>
  )
}

/* ------------------------------------------------------------------ V10 freezers */

// Frosty tints for the six freezer-type tiles (light only), cycled.
const ICE_TINTS = [
  'bg-[linear-gradient(160deg,#ffffff_5%,#dcf2fa_100%)]',
  'bg-[linear-gradient(160deg,#ffffff_5%,#e2ecfd_100%)]',
  'bg-[linear-gradient(160deg,#ffffff_5%,#d9f3ef_100%)]',
]
const COL_ICON = [Archive, Refrigerator]

/** "Chest or upright freezer?": shadcn Table in a Card, then the "How big?" note and its two links. */
function CompareCard({ c }: { c: Model['freezers']['compare'] }) {
  const [feature, ...cols] = c.head
  return (
    <Card className="gap-0! overflow-hidden py-0! shadow-[0_28px_60px_-36px_rgba(8,145,178,0.6)]!">
      <div className={compactClassName("flex items-center gap-3 border-b bg-gradient-to-r from-[#e8f6fb] to-white px-5 py-4")}>
        <span className={compactClassName("flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#0e7490] shadow-xs ring-1 ring-[#0891b2]/20")}>
          <Scale aria-hidden="true" className="size-5" />
        </span>
        <h3 className={compactClassName("text-xl font-semibold tracking-tight text-neutral-950 md:text-2xl")}>{c.title}</h3>
      </div>
      <Table className="table-fixed">
        <TableCaption className="sr-only">{c.caption}</TableCaption>
        <TableHeader>
          <TableRow className="bg-[#f6fbfd] hover:bg-[#f6fbfd]!">
            <TableHead scope="col" className="h-11! w-[26%] px-2.5! text-xs! font-semibold! uppercase tracking-[0.12em] text-neutral-500! whitespace-normal! sm:w-[29%] sm:px-5!">
              {feature}
            </TableHead>
            {cols.map((h, k) => {
              const I = COL_ICON[k] ?? Archive
              return (
                <TableHead key={h} scope="col" className="h-11! px-2.5! whitespace-normal! sm:px-4!">
                  <span className={compactClassName(cn('inline-flex items-center gap-1 rounded-full px-2 py-1 text-[13px] font-semibold sm:gap-1.5 sm:px-2.5', k === 0 ? 'bg-[#dff3fa] text-[#0b5468]' : 'bg-[#e4edfd] text-[#0a3f8f]'))}>
                    <I aria-hidden="true" className="size-3.5" />
                    {h}
                  </span>
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {c.rows.map(([label, ...cells]) => (
            <TableRow key={label} className="hover:bg-[#f6fbfd]!">
              <TableHead scope="row" className="h-auto! px-2.5! py-3! align-top! text-[13px] font-semibold! text-neutral-900! whitespace-normal! sm:px-5!">
                {label}
              </TableHead>
              {cells.map((v, k) => (
                <TableCell key={k} className="px-2.5! py-3! align-top! text-[13px] leading-snug text-neutral-700 whitespace-normal! sm:px-4! sm:text-sm">
                  {v}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className={compactClassName("grid gap-4 border-t bg-white p-5")}>
        <Alert role="note" className="border-[#0891b2]/25! bg-[#f0f9fc]! text-[#0e7490]!">
          <Ruler aria-hidden="true" />
          <AlertTitle className="font-bold! text-neutral-950!">{c.cuftLead}</AlertTitle>
          <AlertDescription className="text-neutral-700!">{c.cuft}</AlertDescription>
        </Alert>
        <div className={compactClassName("flex flex-wrap gap-2")}>
          {c.links.map((l) => <MoreLink key={l.href} link={l} />)}
        </div>
      </div>
    </Card>
  )
}

export function Freezers({ model }: { model: Model }) {
  const m = model.freezers
  return (
    <Section aria={m.aria} v2="V10" tone="ice" className="overflow-hidden">
      <Glow className="-top-40 right-[-12%] size-[560px] bg-[radial-gradient(circle,rgba(8,145,178,0.16),transparent_65%)]" />
      <Glow className="top-[38%] -left-48 size-[460px] bg-[radial-gradient(circle,rgba(4,105,240,0.08),transparent_65%)]" />
      <div className={compactClassName("relative")}>
        <SectionHeader head={m.head} tone="ice" />
        <div className={compactClassName("grid gap-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:items-start lg:gap-8")}>
          <ul className={compactClassName("grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4")}>
            {m.tiles.map((t, k) => (
              <li key={t.href}>
                <ShopTile
                  t={t}
                  tint={ICE_TINTS[k % ICE_TINTS.length]}
                  hover="hover:shadow-[0_20px_40px_-22px_rgba(8,145,178,0.55)] hover:ring-[#0891b2]/35"
                  arrow="text-[#0891b2]"
                />
              </li>
            ))}
          </ul>
          <CompareCard c={m.compare} />
        </div>
        <ul className={compactClassName("mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4")}>
          {m.products.map((p) => (
            <li key={p.fin}>
              <ProductCard p={p} />
            </li>
          ))}
        </ul>
        <ChipRows rows={m.chiprows} className="mt-8!" />
        <div className={compactClassName("mt-10")}>
          <ProductRail products={m.deep.products} title={m.deep.title} viewAll={m.deep.link} autoplay={5000} />
        </div>
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V10b upright promo + freezer colours */

// v2's own swatch fills (read from its stylesheet), used when the model carries no inline style.
const SWATCH: Record<string, string> = {
  white: 'linear-gradient(135deg,#ffffff,#eef1f5)',
  black: '#0c0d10',
  'stainless steel': 'linear-gradient(135deg,#f2f4f7 0%,#b9c0cb 45%,#e8ebf0 100%)',
  stainless: 'linear-gradient(135deg,#f2f4f7 0%,#b9c0cb 45%,#e8ebf0 100%)',
  gray: 'linear-gradient(135deg,#9aa0a8,#7d838c)',
  grey: 'linear-gradient(135deg,#9aa0a8,#7d838c)',
  silver: 'linear-gradient(135deg,#d4d7dc,#aab0b9)',
  red: '#d61318',
}

/** Read background / background-color / background-image out of v2's inline CSS string. */
function parseSwatch(style: string): React.CSSProperties | null {
  const out: React.CSSProperties = {}
  for (const decl of style.split(';')) {
    const at = decl.indexOf(':')
    if (at < 0) continue
    const prop = decl.slice(0, at).trim().toLowerCase()
    const val = decl.slice(at + 1).replace(/!important/i, '').trim()
    if (!val) continue
    if (prop === 'background') out.background = val
    else if (prop === 'background-color') out.backgroundColor = val
    else if (prop === 'background-image') out.backgroundImage = val
  }
  return Object.keys(out).length ? out : null
}

function swatchStyle(c: { label: string; style: string }): React.CSSProperties {
  const parsed = parseSwatch(c.style || '')
  if (parsed) return parsed
  const key = c.label.trim().toLowerCase()
  if (SWATCH[key]) return { background: SWATCH[key] }
  // An unknown single-word colour name is a valid CSS colour; anything else gets a neutral steel fill.
  return { background: /^[a-z]+$/.test(key) ? key : 'linear-gradient(135deg,#e5e7eb,#cbd2db)' }
}

export function Upright({ model }: { model: Model }) {
  const m = model.upright
  return (
    <Section aria={m.aria} v2="V10b" tone="white">
      <div className={compactClassName("relative overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#fff7ef_0%,#ffe8d4_55%,#ffd9b8_100%)] ring-1 ring-[#ff9951]/25")}>
        <Glow className="top-1/2 -left-24 size-[440px] -translate-y-1/2 bg-[radial-gradient(circle,rgba(255,153,81,0.5),transparent_66%)]" />
        <Glow className="-top-28 -right-20 size-[340px] bg-[radial-gradient(circle,rgba(255,255,255,0.95),transparent_70%)]" />
        <div className={compactClassName("relative grid items-center gap-8 px-6 py-10 sm:px-10 md:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] md:gap-10 md:py-14 lg:px-16")}>
          <div className={compactClassName("flex justify-center")}>
            <Img
              src={m.img.src}
              alt={m.img.alt}
              className="h-56 w-auto drop-shadow-[0_22px_26px_rgba(67,31,14,0.32)] sm:h-64 md:h-80"
            />
          </div>
          <div className={compactClassName("max-w-xl")}>
            <Eyebrow tone="peach">{m.eyebrow}</Eyebrow>
            <h2 className={compactClassName("mt-3 text-balance text-3xl font-bold tracking-tight text-neutral-950 md:text-[2.75rem] md:leading-[1.06]")}>{m.title}</h2>
            <p className={compactClassName("mt-4 text-pretty text-base leading-relaxed text-neutral-700 md:text-lg")}>{m.text}</p>
            <div className={compactClassName("mt-7 flex flex-wrap gap-3")}>
              {m.ctas.map((l, k) => (
                <Button
                  key={l.href}
                  asChild
                  variant={k === 0 ? 'ctaPrimary' : 'outline'}
                  size="lg"
                  className={cn(
                    'h-12! rounded-full! px-7! text-[15px]! font-semibold!',
                    k === 0
                      ? 'shadow-[0_12px_26px_-12px_rgba(180,83,9,0.7)]!'
                      : 'border-neutral-900/15! bg-white! text-neutral-900! hover:bg-[#fff7ef]!',
                  )}
                >
                  <DestinationLink href={l.href} aria-label={l.aria}>
                    {words(l.text)}
                    <ArrowRight aria-hidden="true" />
                  </DestinationLink>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={compactClassName("mt-8")}>
        <SubHeader title={m.colorsLabel} />
        <ul className={compactClassName("grid grid-cols-3 gap-3 sm:grid-cols-6 sm:gap-4")}>
          {m.colors.map((c) => (
            <li key={c.href}>
              <DestinationLink
                href={c.href}
                aria-label={c.aria}
                className="group flex h-full flex-col items-center gap-2.5 rounded-2xl border border-neutral-200 bg-white px-2 py-4 text-center transition duration-200 hover:border-[#ff9951]/60 hover:shadow-md motion-safe:hover:-translate-y-0.5"
              >
                <span
                  aria-hidden="true"
                  style={swatchStyle(c)}
                  className={compactClassName("size-12 rounded-full shadow-[inset_0_-6px_12px_rgba(0,0,0,0.12),0_8px_16px_-8px_rgba(0,0,0,0.4)] ring-1 ring-black/10 transition-transform duration-300 motion-safe:group-hover:scale-110 sm:size-14")}
                />
                <span className={compactClassName("text-xs font-semibold text-neutral-800 sm:text-sm")}>{c.label}</span>
              </DestinationLink>
            </li>
          ))}
        </ul>
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V11 every room (bento mosaic) */

// md: 3 columns, lg: 6 columns. With v2's size hints and dense flow, both fill a perfect rectangle (18 cells).
const ROOM_SPAN: Record<string, string> = {
  big: 'md:col-span-2 md:row-span-2',
  tall: 'md:row-span-2',
  wide: 'md:col-span-2',
}

function RoomTile({ t }: { t: Model['rooms']['tiles'][number] }) {
  const cut = isCutout(t.img.src)
  const big = t.size === 'big'
  return (
    <DestinationLink
      href={t.href}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/5 transition duration-300 hover:shadow-[0_26px_50px_-26px_rgba(120,72,20,0.5)] motion-safe:hover:-translate-y-1"
    >
      <div
        className={compactClassName(cn(
          'relative aspect-[4/3] overflow-hidden md:absolute md:inset-0 md:aspect-auto',
          cut ? 'bg-[radial-gradient(circle_at_50%_38%,#ffffff_0%,#f3eadc_78%)]' : 'bg-[#efe6d8]',
        ))}
      >
        <Img
          src={t.img.src}
          alt={t.img.alt}
          fit={cut ? 'contain' : 'cover'}
          className={cn(
            'absolute inset-0 size-full transition-transform duration-700 motion-safe:group-hover:scale-[1.05]',
            cut && 'p-[10%] mix-blend-multiply md:pb-[30%]',
          )}
        />
        <span
          aria-hidden="true"
          className={compactClassName("absolute top-3 right-3 hidden size-9 items-center justify-center rounded-full bg-white/95 text-neutral-900 opacity-0 shadow-sm ring-1 ring-black/5 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100 md:flex")}
        >
          <ArrowUpRight className="size-4" />
        </span>
      </div>
      <div className={compactClassName("relative flex flex-1 flex-col gap-0.5 px-3 pt-2.5 pb-3 md:absolute md:inset-x-0 md:bottom-0 md:flex-none md:bg-gradient-to-t md:from-white md:via-white/90 md:to-white/0 md:px-4 md:pt-12 md:pb-3.5")}>
        <span className={compactClassName(cn('font-semibold leading-snug tracking-tight text-neutral-950', big ? 'text-base md:text-xl' : 'text-[13px] md:text-[15px]'))}>{t.title}</span>
        <span className={compactClassName(cn('leading-snug text-neutral-600', big ? 'text-xs md:text-sm' : 'text-[11px] md:text-xs'))}>{' '}{t.meta}</span>
      </div>
    </DestinationLink>
  )
}

export function Rooms({ model }: { model: Model }) {
  const m = model.rooms
  return (
    <Section aria={m.aria} v2="V11" tone="sand">
      <SectionHeader head={m.head} tone="sand" />
      <ul className={compactClassName("grid grid-cols-2 gap-3 md:grid-flow-dense md:auto-rows-[172px] md:grid-cols-3 md:gap-4 lg:auto-rows-[200px] lg:grid-cols-6")}>
        {m.tiles.map((t) => (
          <li key={t.href} className={compactClassName(cn('min-w-0', ROOM_SPAN[t.size]))}>
            <RoomTile t={t} />
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ------------------------------------------------------------------ V11b every space */

// One light tint per tile, cycled (sky, peach, mint, lilac, sand, ice).
const PASTELS = ['bg-[#eef5ff]', 'bg-[#fff1e6]', 'bg-[#eafaf1]', 'bg-[#f3efff]', 'bg-[#fdf5e4]', 'bg-[#e8f7fb]']
const PASTEL_ARROW = ['text-[#0454c0]', 'text-[#b54708]', 'text-[#067647]', 'text-[#0454c0]', 'text-[#b54708]', 'text-[#067647]']

export function Spaces({ model }: { model: Model }) {
  const m = model.spaces
  return (
    <Section aria={m.aria} v2="V11b" tone="white">
      <SectionHeader head={{ title: m.title, sub: m.sub }} />
      <ul className={compactClassName("grid grid-cols-3 gap-2.5 sm:gap-3 md:grid-cols-4 lg:grid-cols-6 lg:gap-4")}>
        {m.tiles.map((t, k) => (
          <li key={t.href + t.label}>
            <ShopTile
              t={t}
              tint={PASTELS[k % PASTELS.length]}
              hover="hover:shadow-[0_20px_40px_-24px_rgba(15,23,42,0.4)] hover:ring-black/10"
              arrow={PASTEL_ARROW[k % PASTEL_ARROW.length]}
            />
          </li>
        ))}
      </ul>
    </Section>
  )
}
